import { GridType, CellState, GameConfig } from '../types';

export type RandomFn = () => number;

export const fireSpreadService = {
  createGrid(size: number): GridType {
    return Array(size).fill(null).map(() => 
      Array(size).fill('alive' as CellState)
    );
  },

  initializeGridFromPositions(
    grid: GridType,
    positions: [number, number][]
  ): GridType {
    const newGrid = grid.map((row) => [...row]);
    for (const [row, col] of positions) {
      if (newGrid[row]?.[col] === 'alive') {
        newGrid[row][col] = 'burning';
      }
    }
    return newGrid;
  },

  initializeGrid(
    grid: GridType,
    numStartingFires: number,
    random: RandomFn = Math.random
  ): GridType {
    const size = grid.length;
    const newGrid = grid.map(row => [...row]);
    const firePositions = new Set<string>();

    while (firePositions.size < Math.min(numStartingFires, size * size)) {
      const row = Math.floor(random() * size);
      const col = Math.floor(random() * size);
      const pos = `${row},${col}`;
      
      if (!firePositions.has(pos)) {
        firePositions.add(pos);
        newGrid[row][col] = 'burning';
      }
    }

    return newGrid;
  },

  getAdjacentCells(grid: GridType, row: number, col: number): [number, number][] {
    const size = grid.length;
    const adjacent: [number, number][] = [
      [row - 1, col], // up
      [row + 1, col], // down
      [row, col - 1], // left
      [row, col + 1]  // right
    ];

    return adjacent.filter(([r, c]) => 
      r >= 0 && r < size && c >= 0 && c < size
    );
  },

  updateGrid(
    grid: GridType,
    config: GameConfig,
    random: RandomFn = Math.random
  ): GridType {
    const size = grid.length;
    const newGrid = grid.map(row => [...row]);
    const newBurning = new Set<string>();

    // Process fire spread
    for (let row = 0; row < size; row++) {
      for (let col = 0; col < size; col++) {
        if (grid[row][col] === 'burning') {
          // Check adjacent cells
          const adjacent = this.getAdjacentCells(grid, row, col);
          for (const [adjRow, adjCol] of adjacent) {
            if (grid[adjRow][adjCol] === 'alive') {
              const burningNeighbors = this.getAdjacentCells(grid, adjRow, adjCol)
                .filter(([r, c]) => grid[r][c] === 'burning').length;
              
              for (let i = 0; i < burningNeighbors; i++) {
                if (random() < config.spreadChance) {
                  newBurning.add(`${adjRow},${adjCol}`);
                  break;
                }
              }
            }
          }

          // Check teleport
          if (random() < config.teleportChance) {
            const teleRow = Math.floor(random() * size);
            const teleCol = Math.floor(random() * size);
            if (grid[teleRow][teleCol] === 'alive') {
              newBurning.add(`${teleRow},${teleCol}`);
            }
          }

          // Current burning cell becomes burned
          newGrid[row][col] = 'burned';
        }
      }
    }

    // Apply new burning cells
    for (const pos of newBurning) {
      const [row, col] = pos.split(',').map(Number);
      newGrid[row][col] = 'burning';
    }

    return newGrid;
  },

  checkGameOver(grid: GridType, silent = false): 'win' | 'lose' | null {
    let hasAlive = false;
    let hasBurning = false;
    let aliveCells = 0;
    let burningCells = 0;
    let burnedCells = 0;

    // Count all cell states
    for (const row of grid) {
        for (const cell of row) {
            switch (cell) {
                case 'alive':
                    hasAlive = true;
                    aliveCells++;
                    break;
                case 'burning':
                    hasBurning = true;
                    burningCells++;
                    break;
                case 'burned':
                    burnedCells++;
                    break;
            }
        }
    }

    if (!silent) {
      console.log('Grid State:', {
        aliveCells,
        burningCells,
        burnedCells,
        totalCells: grid.length * grid.length,
      });
    }

    if (!hasAlive) {
      if (!silent) {
        console.log('💀 DEFEAT!', {
          reason: 'No surviving trees remain',
          burningTrees: burningCells,
          burnedTrees: burnedCells,
          totalDestroyed: burningCells + burnedCells,
        });
      }
      return 'lose';
    }

    if (!hasBurning && hasAlive) {
      if (!silent) {
        console.log('🎉 VICTORY!', {
          reason: 'Fire contained with surviving trees',
          survivingTrees: aliveCells,
          burnedTrees: burnedCells,
          survivalRate: `${((aliveCells / (grid.length * grid.length)) * 100).toFixed(1)}%`,
        });
      }
      return 'win';
    }

    // Game continues if neither condition is met
    return null;
  },

  calculateSurvival(grid: GridType): number {
    let aliveCount = 0;
    const totalCells = grid.length * grid.length;

    for (const row of grid) {
      for (const cell of row) {
        if (cell === 'alive') aliveCount++;
      }
    }

    return (aliveCount / totalCells) * 100;
  }
};
